import React, { useState, useCallback, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTopbar } from '../../contexts/TopbarContext';
import { useAllReminders, UnifiedReminderRow, SOURCE_LABELS, ReminderSourceType } from './useData';
import { formatDate, formatDateTime } from '../../config/appConfig';
import { Badge } from '../../components/ui';
import api from '../../services/api';
import SupportAgreementRenewalRemindersPanel from '../crm-support-agreements/SupportAgreementRenewalRemindersPanel';
import ServiceRenewalRemindersPanel from '../client-services/ServiceRenewalRemindersPanel';
import ClientInvoicePaymentRemindersPanel from '../client-invoices/ClientInvoicePaymentRemindersPanel';
import OfferFollowUpsPanel from '../offers/OfferFollowUpsPanel';

// Status badge colors/labels used across reminder types (service, support agreement, invoice, offer)
const STATUS_COLORS: Record<string, string> = {
  pending: '#f59e0b',
  renewed: '#10b981',
  terminating: '#ef4444',
  no_response: '#64748b',
  will_proceed: '#3b82f6',
  declined: '#ef4444',
  will_pay: '#3b82f6',
  paid: '#10b981',
  disputed: '#f59e0b',
  accepted: '#10b981',
  expired: '#64748b',
  will_decide: '#8b5cf6',
};
const STATUS_LABELS: Record<string, string> = {
  pending: 'Pending',
  renewed: 'Renewed',
  terminating: 'Terminating',
  no_response: 'No response',
  will_proceed: 'Will proceed',
  declined: 'Declined',
  will_pay: 'Will pay',
  paid: 'Paid',
  disputed: 'Disputed',
  accepted: 'Accepted',
  expired: 'Expired',
  will_decide: 'Will decide',
};

function renderStatusBadge(status: string | null) {
  if (!status) return null;
  const key = status.toLowerCase().replace(/\s+/g, '_');
  const label = STATUS_LABELS[key] ?? status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
  const hex = STATUS_COLORS[key] ?? STATUS_COLORS.no_response;
  return <Badge color={hex}>{label}</Badge>;
}

const CHANNEL_COLORS: Record<string, string> = {
  email: '#3b82f6',
  phone: '#10b981',
  other: '#64748b',
};

function renderChannelBadge(channel: string | null) {
  if (!channel?.trim()) return null;
  const key = channel.toLowerCase().trim();
  const label = key.charAt(0).toUpperCase() + key.slice(1);
  const hex = CHANNEL_COLORS[key] ?? CHANNEL_COLORS.other;
  return <Badge color={hex}>{label}</Badge>;
}

const SOURCE_ICONS: Record<ReminderSourceType, React.ReactNode> = {
  service: (
    <svg className="w-5 h-5 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
    </svg>
  ),
  support_agreement: (
    <svg className="w-5 h-5 text-emerald-600 dark:text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
    </svg>
  ),
  invoice: (
    <svg className="w-5 h-5 text-amber-600 dark:text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
    </svg>
  ),
  offer: (
    <svg className="w-5 h-5 text-violet-600 dark:text-violet-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
    </svg>
  ),
};

const REMINDERS_BELL_ICON = (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
  </svg>
);

const VIEW_ICON = (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
  </svg>
);

const List: React.FC = () => {
  const { rows, isLoading, isError, mutate } = useAllReminders();
  const { setTitleOverride } = useTopbar();
  const [reminderPanelType, setReminderPanelType] = useState<ReminderSourceType | null>(null);
  const [reminderParentId, setReminderParentId] = useState<number | null>(null);
  const [reminderParentData, setReminderParentData] = useState<unknown>(null);
  const [reminderPanelOpen, setReminderPanelOpen] = useState(false);
  const [loadingParent, setLoadingParent] = useState(false);

  const openRemindersModal = useCallback((row: UnifiedReminderRow) => {
    const id = typeof row.parentId === 'string' ? parseInt(row.parentId, 10) : row.parentId;
    if (Number.isNaN(id)) return;
    setReminderPanelType(row.sourceType);
    setReminderParentId(id);
    setReminderParentData(null);
    setReminderPanelOpen(true);
    setLoadingParent(true);
  }, []);

  useEffect(() => {
    if (!reminderPanelOpen || !reminderPanelType || reminderParentId == null) return;
    const id = reminderParentId;
    let cancelled = false;
    const endpoints: Record<ReminderSourceType, string> = {
      service: `/crm-clients-services/${id}`,
      support_agreement: `/crm-support-agreements/${id}`,
      invoice: `/crm-clients-invoices/${id}`,
      offer: `/offers/${id}`,
    };
    const url = endpoints[reminderPanelType];
    if (!url) {
      setLoadingParent(false);
      return;
    }
    api
      .get(url)
      .then((res) => {
        if (!cancelled) {
          setReminderParentData(res.data);
        }
      })
      .catch(() => {
        if (!cancelled) setReminderParentData(null);
      })
      .finally(() => {
        if (!cancelled) setLoadingParent(false);
      });
    return () => {
      cancelled = true;
    };
  }, [reminderPanelOpen, reminderPanelType, reminderParentId]);

  const closeRemindersModal = useCallback(() => {
    setReminderPanelOpen(false);
    setReminderPanelType(null);
    setReminderParentId(null);
    setReminderParentData(null);
    mutate();
  }, [mutate]);

  useEffect(() => {
    setTitleOverride('All Reminders');
    return () => {
      setTitleOverride(undefined);
    };
  }, [setTitleOverride]);

  if (isError) {
    return (
      <div className="p-6">
        <div className="rounded-lg border border-red-200 bg-red-50 dark:bg-red-900/20 dark:border-red-800 p-4 text-red-700 dark:text-red-300">
          Failed to load reminders. Please try again.
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full min-h-0 p-4 md:p-6">
      <div className="flex-1 min-h-0 overflow-auto rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
        {isLoading ? (
          <div className="flex items-center justify-center py-12 text-gray-500 dark:text-gray-400">Loading reminders…</div>
        ) : rows.length === 0 ? (
          <div className="flex items-center justify-center py-12 text-gray-500 dark:text-gray-400">No reminders found.</div>
        ) : (
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-700/50 sticky top-0 z-10">
              <tr>
                <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider w-0">
                  Actions
                </th>
                <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider w-0">
                  Source
                </th>
                <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Date
                </th>
                <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Status
                </th>
                <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Parent
                </th>
                <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Client
                </th>
                <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Channel
                </th>
                <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider max-w-[180px]">
                  Response note
                </th>
                <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Response at
                </th>
                <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Created by
                </th>
                <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Extra
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700 bg-white dark:bg-gray-800">
              {rows.map((row: UnifiedReminderRow) => (
                <tr key={row.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/30">
                  <td className="px-4 py-3 text-sm whitespace-nowrap">
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => openRemindersModal(row)}
                        className="inline-flex items-center justify-center rounded-md p-1.5 text-indigo-700 bg-indigo-50 hover:bg-indigo-100 dark:text-indigo-300 dark:bg-indigo-900/30 dark:hover:bg-indigo-900/50"
                        title="Open reminders"
                      >
                        {REMINDERS_BELL_ICON}
                      </button>
                      <Link
                        to={row.linkPath}
                        className="inline-flex items-center justify-center rounded-md p-1.5 text-gray-700 bg-gray-100 hover:bg-gray-200 dark:text-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600"
                        title="Open record"
                      >
                        {VIEW_ICON}
                      </Link>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm whitespace-nowrap">
                    <div className="flex items-center gap-2">
                      {SOURCE_ICONS[row.sourceType]}
                      <span className="text-gray-900 dark:text-gray-100 font-medium">{SOURCE_LABELS[row.sourceType]}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300 whitespace-nowrap">
                    {formatDateTime(row.date)}
                  </td>
                  <td className="px-4 py-3 text-sm whitespace-nowrap">{renderStatusBadge(row.status)}</td>
                  <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300 max-w-[200px]" title={row.parentDescription}>
                    <span className="block truncate">{row.parentDescription}</span>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300 whitespace-nowrap">
                    {row.clientName}
                  </td>
                  <td className="px-4 py-3 text-sm whitespace-nowrap">
                    {renderChannelBadge(row.channel)}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300 max-w-[180px]" title={row.responseNote ?? undefined}>
                    <span className="block truncate">{row.responseNote ?? ''}</span>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300 whitespace-nowrap">
                    {row.responseAt ? formatDateTime(row.responseAt) : ''}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300 whitespace-nowrap">
                    {row.createdByLabel}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300 whitespace-nowrap">
                    {row.parentExtraLabel && row.parentExtraDate ? (
                      <span title={row.parentExtraDate}>
                        {row.parentExtraLabel} {formatDate(row.parentExtraDate)}
                      </span>
                    ) : (
                      ''
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {reminderPanelOpen && reminderPanelType === 'support_agreement' && (
        <SupportAgreementRenewalRemindersPanel
          open
          onClose={closeRemindersModal}
          agreement={reminderParentData as import('../crm-support-agreements/useData').SupportAgreement | null}
          onReminderAdded={mutate}
          variant="modal"
        />
      )}
      {reminderPanelOpen && reminderPanelType === 'service' && (
        <ServiceRenewalRemindersPanel
          open
          onClose={closeRemindersModal}
          serviceItem={reminderParentData as import('../client-services/useData').ClientService | null}
          onReminderAdded={mutate}
          variant="modal"
        />
      )}
      {reminderPanelOpen && reminderPanelType === 'invoice' && (
        <ClientInvoicePaymentRemindersPanel
          open
          onClose={closeRemindersModal}
          invoice={reminderParentData as import('../client-invoices/useData').ClientInvoice | null}
          onReminderAdded={mutate}
          variant="modal"
        />
      )}
      {reminderPanelOpen && reminderPanelType === 'offer' && (
        <OfferFollowUpsPanel
          open
          onClose={closeRemindersModal}
          offer={reminderParentData as import('../offers/useData').Offer | null}
          onFollowUpAdded={mutate}
          variant="modal"
        />
      )}
      {reminderPanelOpen && loadingParent && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" aria-busy="true">
          <div className="rounded-lg bg-white dark:bg-gray-800 px-6 py-4 shadow-xl text-gray-700 dark:text-gray-200">
            Loading…
          </div>
        </div>
      )}
    </div>
  );
};

export default List;
